import { useEffect, useState } from "react";
import { Plus, Trash2, CalendarCheck, Timer } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

function Ring({ pct, size = 96, stroke = 10, color = "#F59E0B", label, sub }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F5F5F4" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontFamily="Outfit" fontSize={size / 4} fontWeight="700" fill="#1C1917">
          {pct}%
        </text>
      </svg>
      <div>
        <div className="font-display text-lg font-semibold">{label}</div>
        <div className="text-sm text-stone-500">{sub}</div>
      </div>
    </div>
  );
}

const emptyEntry = { date: new Date().toISOString().slice(0, 10), hours: 4, reason: "Field trip", notes: "" };

export default function AttendanceCard({ studentId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [entry, setEntry] = useState(emptyEntry);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/students/${studentId}/attendance`);
      setData(data);
    } catch {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [studentId]);

  const add = async () => {
    setSaving(true);
    try {
      await api.post(`/students/${studentId}/attendance/manual`, {
        date: entry.date,
        hours: Number(entry.hours),
        reason: entry.reason,
        notes: entry.notes,
      });
      toast.success("Attendance logged");
      setOpen(false);
      setEntry(emptyEntry);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this attendance entry?")) return;
    try {
      await api.delete(`/students/${studentId}/attendance/manual/${id}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!data) return null;

  return (
    <div className="bg-white border border-stone-200 rounded-3xl p-8 shadow-sm" data-testid="attendance-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold flex items-center gap-2">
            <CalendarCheck className="w-6 h-6 text-amber-600" /> Attendance & Hours
          </h2>
          <p className="text-sm text-stone-500 mt-1">
            Florida Statute 1002.41 requires <strong>180 days</strong> & <strong>720 hours</strong>. Completed lessons count automatically; log field trips or co-op days manually.
          </p>
          <div className="mt-1 text-xs text-stone-500">School year {data.school_year} ({data.start} → {data.end})</div>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-attendance-btn">
          <Plus className="w-4 h-4 mr-2" /> Log day
        </Button>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-6" data-testid="attendance-rings">
        <Ring pct={data.days_pct} color="#F59E0B" label={`${data.days_logged} / ${data.days_target} days`} sub={`${data.lesson_days} lesson + ${data.manual_days} manual`} />
        <Ring pct={data.hours_pct} color="#10B981" label={`${data.hours_logged} / ${data.hours_target} hrs`} sub={`${data.lesson_hours} lesson + ${data.manual_hours} manual`} />
      </div>

      {data.manual_entries.length > 0 && (
        <div className="mt-6">
          <div className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500 mb-2">Manual entries</div>
          <div className="divide-y divide-stone-100 max-h-60 overflow-y-auto">
            {data.manual_entries.map((e) => (
              <div key={e.id} className="py-2 flex items-center gap-3" data-testid={`manual-entry-${e.id}`}>
                <div className="text-xs font-mono w-24">{e.date}</div>
                <div className="text-sm flex-1 min-w-0 truncate">
                  <span className="font-medium">{e.reason}</span>
                  {e.notes && <span className="text-stone-500"> · {e.notes}</span>}
                </div>
                <div className="text-sm text-stone-700 font-medium"><Timer className="w-3.5 h-3.5 inline mr-1" />{e.hours} hr</div>
                <Button size="sm" variant="ghost" onClick={() => remove(e.id)} className="rounded-full text-red-600" data-testid={`delete-attendance-${e.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="attendance-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Log an attendance day</DialogTitle>
            <DialogDescription>For field trips, co-op days, life-skills days, or any instruction not tied to a scheduled lesson.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={entry.date} onChange={(e) => setEntry({ ...entry, date: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="attendance-date-input" />
              </div>
              <div>
                <Label>Hours</Label>
                <Input type="number" step="0.25" min="0" max="12" value={entry.hours} onChange={(e) => setEntry({ ...entry, hours: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="attendance-hours-input" />
              </div>
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={entry.reason} onChange={(e) => setEntry({ ...entry, reason: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="attendance-reason-input" />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={entry.notes} onChange={(e) => setEntry({ ...entry, notes: e.target.value })} className="mt-2 rounded-xl min-h-[60px]" data-testid="attendance-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={add} disabled={saving} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="attendance-save-btn">
              {saving ? "Saving…" : "Log day"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
